import { Components, Theme } from '@mui/material/styles';
import type { } from '@mui/x-data-grid-premium/themeAugmentation';
import type { } from '@mui/x-tree-view/themeAugmentation';

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		loginButton: true;
		meinBereich: true;
		backButton: true;
	}
}

const createComponents = (theme: Theme): Components => ({
	/**
	 * Typography
	 */
	MuiTypography: {
		styleOverrides: {
			root: {
				wordWrap: 'break-word',
				'&.MuiTypography-subtitle2': {
					color: theme.palette.text.secondary
				}
			},
		},
	},
	/**
	 * MUI Paper
	 */
	MuiPaper: {
		styleOverrides: {
			root: {
				boxShadow: 'unset',
				backgroundImage: 'unset',
			},
		},
	},
	/**
	 * Container
	 */
	MuiContainer: {
		defaultProps: {
			component: 'section',
		},
		styleOverrides: {
			root: {
				marginTop: '2rem',
				[theme.breakpoints.up('lg')]: {
					width: '75rem',
				},
			},
		},
	},
	/**
	 * App Bar (Header-Navigation Menu)
	 */
	MuiAppBar: {
		styleOverrides: {
			root: {
				background: theme.palette.background.paper,
			},
		},
	},
	/**
	 * Mui badge
	 */
	MuiBadge: {
		styleOverrides: {
			root: {
				'& .MuiBadge-badge': {
					// transform: `scale(0.9) translate(0%, 50%)`,
					backgroundColor: '#fff',
					right: '-5px',
					top: '-10px'
				},
			},
		},
	},

	/**
	 * Snackbar
	 */
	MuiSnackbar: {
		styleOverrides: {
			root: {
				'@media (min-width: 0px)': {
					pointerEvents: 'auto',
					top: '9rem',
					left: '0',
					right: 0,
					width: '100%',
					maxWidth: '75rem',
					margin: 'auto',
					padding: '0 1.875rem',
					boxShadow: 'unset',
					'& .MuiAlert-root': {
						width: 'auto',
						boxShadow: '0px 3px 12px #42788767',
					},
				},
			},
		},
	},
	/**
	 * Accordion
	 */
	MuiAccordion: {
		styleOverrides: {
			root: { boxShadow: 'unset', backgroundImage: 'unset', margin: '0.5rem 0' },
		},
	},
	/**
	 * Button
	 */
	MuiButton: {
		defaultProps: {
			variant: 'contained',
		},
		variants: [
			{
				props: { variant: 'loginButton' },
				style: {
					width: '100%',
					backgroundColor: theme.palette.primary.main,
					border: `1px solid ${theme.palette.primary.main}`,
					color: '#fff',
					'&:hover': {
						bgColor: theme.palette.background.paper,
						border: `1px solid ${theme.palette.primary.main}`,
						color: theme.palette.primary.main,
					},
				},
			},
			{
				props: { variant: 'meinBereich' },
				style: {
					width: '100%',
					backgroundColor: 'transparent',
					border: `1px solid ${theme.palette.primary.main}`,
					borderRadius: '4px',
					color: theme.palette.text.primary,
					padding: '0.75rem 1.375rem', // 12px 22px
					fontSize: '0.8rem',
					maxWidth: '100%',
					justifyContent: 'flex-start'
				},
			},
			{
				props: { variant: 'backButton' },
				style: {
					backgroundColor: '#B5B1AFCC',
					color: '#1B1919',
					border: '4px solid #646260',
					'&:hover': {
						color: '#fff'
					}
				},
			},
		],
		styleOverrides: {
			root: {
				borderRadius: '3rem',
				textTransform: 'none',
				maxWidth: '17rem',
				'&.Mui-disabled': {
					backgroundColor: theme.palette.text.disabled,
					color: theme.palette.text.secondary,
					borderColor: theme.palette.text.disabled
				}
			},
		},
	},
	/**
	 * Card
	 */
	MuiCardActions: {
		styleOverrides: {
			root: { justifyContent: 'flex-end', padding: '1rem 1rem 2rem 1rem' },
		},
	},
	/**
 * Checkbox
 */
	MuiFormControlLabel: {
		styleOverrides: {
			root: {
				'& .MuiFormControlLabel-label.Mui-disabled': {
					color: theme.palette.text.secondary
				}
			}
		}
	},
	/**
	 * TextField
	 */
	MuiTextField: {
		styleOverrides: {
			root: {
				marginTop: '20px',
				marginRight: '15px',
			},
		},
	},
	MuiInputBase: {
		styleOverrides: {
			root: {
				'& .Mui-disabled': {
					cursor: 'not-allowed',
				},
			},
		},
	},
	MuiInputLabel: {
		defaultProps: {
			shrink: true,
		},
		styleOverrides: {
			root: {
				color: theme.palette.text.primary,
				padding: "0",
				whiteSpace: "normal",
				position: "static",
				transform: "none",
				"&.Mui-disabled": {
					color: theme.palette.text.secondary,
				},
				"&.Mui-error": {
					color: theme.palette.text.primary,
				},
			},
		},
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				backgroundColor: theme.palette.text.disabled,
				"legend span": {
					display: "none",
				},
				"&::placeholder": {
					color: "#0ff",
				},
				"&.Mui-disabled": {
					backgroundColor: theme.palette.text.disabled,
					color: theme.palette.text.secondary,
					"& input": {
						'-webkit-text-fill-color': theme.palette.text.secondary,
					},
					"& textarea": {
						'-webkit-text-fill-color': theme.palette.text.secondary,
					},
					"& svg": {
						display: "none",
					},
					"& .MuiOutlinedInput-notchedOutline": {
						//borderColor: "#3e3939"
					},
				},
				"& .MuiSelect-select.Mui-disabled": {
					backgroundColor: theme.palette.text.disabled,
					color: theme.palette.text.secondary,
					'-webkit-text-fill-color': theme.palette.text.secondary,
				},
				"&.Mui-error .MuiOutlinedInput-notchedOutline": {
					borderColor: "#FF5C5C"
				},
				fieldset: {
					borderWidth: 0,
					borderColor: theme.palette.text.secondary,
				},
			},
		},
	},
	MuiFormHelperText: {
		styleOverrides: {
			root: {
				"&.Mui-error": {
					color: "#FF5C5C"
				}
			}
		}
	},
	/**
	 * Mui Tabs
	 */
	MuiTabs: {
		styleOverrides: {
			root: {
				'& .MuiTabs-indicator': {
					left: 0,
					width: '9px',
					borderTopLeftRadius: '80px 80px',
					borderBottomLeftRadius: '80px 80px',
				}
			},
		},
	},
	MuiTab: {
		styleOverrides: {
			root: {
				alignItems: 'center',
				justifyContent: 'flex-start',
				textTransform: 'none',
				fontSize: '1rem',
				borderTopLeftRadius: '9px',
				borderBottomLeftRadius: '9px',
				minHeight: '3rem',
			},
		},
	},
	/**
	 * MUI Dialog
	 */
	MuiDialogActions: {
		styleOverrides: {
			root: {
				justifyContent: 'space-between',
				// display: 'unset',
				padding: '1rem',
			},
		},
	},
	/**
	 * MUI DataGrid
	 */
	MuiDataGrid: {
		styleOverrides: {
			root: {
				borderRadius: '1rem',
				backgroundColor: theme.palette.background.paper,
				'& .message-read-status--false--message-folder-Posteingang': {
					fontFamily: 'inherit',
					fontWeight: '900',
				},
				'& .MuiDataGrid-cell': { cursor: 'pointer' },
			},
		},
	},
	MuiTable: {
		styleOverrides: {
			root: {
				'&.editable-table .MuiFormControl-root': {
					marginTop: 0
				}
			}
		}
	},
	/**
	 * MUI MuiRichTreeView
	 */
	MuiTreeItem2: {
		styleOverrides: {
			root: {
				'&:before': {
					content: '""',
					position: 'absolute',
					left: '12px',
					top: '-4px',
					bottom: 0,
					width: '2px',
					backgroundColor: theme.palette.primary.main,
				},
				'&:first-of-type:before': {
					top: '10px',
				},
				'&:last-child:before': {
					bottom: '10px',
				},
			},
		},
	},
});

export default createComponents;